import React from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box, Divider } from '@mui/material';

interface MedicalDisclaimerModalProps {
  open: boolean;
  onClose: () => void;
  onAccept?: () => void;
}

export const MedicalDisclaimerModal: React.FC<MedicalDisclaimerModalProps> = ({ open, onClose, onAccept }) => {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper" aria-labelledby="disclaimer-dialog-title">
      <DialogTitle id="disclaimer-dialog-title" className="bg-slate-900 text-white font-bold text-lg md:text-xl py-4">
        ⚖️ TERMS AND CONDITIONS AGREEMENT AND MEDICAL DISCLAIMER (v1.2-2026)
      </DialogTitle>
      
      <DialogContent dividers className="space-y-6 text-gray-800 text-sm md:text-base leading-relaxed p-6">
        <Typography variant="body2" className="italic text-gray-600 bg-slate-100 p-3 rounded-md border-l-4 border-blue-600">
          By checking the box &quot;I have read and agree to the Terms and Conditions and Medical Disclaimer&quot;, you (hereinafter, &quot;the Patient&quot; or &quot;the User&quot;) explicitly, voluntarily, and knowingly accept the following clauses prior to receiving any homeopathic consultation through this application:
        </Typography>

        <Box>
          <Typography variant="h6" component="h3" className="font-semibold text-blue-900 mb-1">
            1. NATURE OF HOMEOPATHY AND COMPLEMENTARY THERAPIES
          </Typography>
          <Typography variant="body2" className="text-gray-700">
            The User acknowledges and understands that homeopathy is a discipline of alternative/complementary medicine. The remedies and recommendations provided through this application are intended to support general well-being and bodily balance according to homeopathic principles, and <strong>DO NOT</strong> constitute emergency medicine nor an absolute replacement for conventional (allopathic) medicine.
          </Typography>
        </Box>

        <Divider />

        <Box className="bg-red-50 p-4 rounded-lg border border-red-200">
          <Typography variant="h6" component="h3" className="font-semibold text-red-900 mb-1 flex items-center gap-2">
            <span>⚠️</span> 2. NO EMERGENCY MEDICAL CARE
          </Typography>
          <Typography variant="body2" className="text-red-900 font-medium">
            This application and its healthcare practitioners <strong>ARE NOT</strong> equipped or designed to handle medical emergencies or urgent conditions of any kind.
          </Typography>
          <Typography variant="body2" className="text-red-800 mt-2">
            If you are experiencing a medical emergency, severe symptoms, acute pain, breathing difficulty, or any other critical health condition, you must immediately contact your local emergency services (e.g., <strong>911</strong>, <strong>112</strong>, or local equivalent) or visit the nearest hospital emergency room.
          </Typography>
        </Box>

        <Divider />

        <Box>
          <Typography variant="h6" component="h3" className="font-semibold text-blue-900 mb-1">
            3. NO INTERRUPTION OF CONVENTIONAL TREATMENTS
          </Typography>
          <Typography variant="body2" className="text-gray-700">
            The User agrees not to discontinue, alter, delay, or modify any treatment, prescribed medication, or diagnosis issued by their primary care physician or conventional medical specialist without prior consultation and supervision from said healthcare professional. A homeopathic consultation does not replace diagnostic tests (blood work, X-rays, etc.) or routine medical check-ups.
          </Typography>
        </Box>

        <Divider />

        <Box>
          <Typography variant="h6" component="h3" className="font-semibold text-blue-900 mb-1">
            4. REQUIRED PROFESSIONAL SUPERVISION OF RECOMMENDED REMEDIES
          </Typography>
          <Typography variant="body2" className="text-gray-700">
            Any homeopathic remedy, potency, dosage, or regimen suggested or recommended through this application <strong>MUST ALWAYS BE SUPERVISED BY A QUALIFIED SPECIALIST IN HOMEOPATHIC MEDICINE</strong>.
          </Typography>
          <Typography variant="body2" className="text-gray-700 mt-2">
            The User agrees not to self-medicate or alter the recommended dosages, potencies, or frequency of administration without prior direct evaluation and oversight by their consulting homeopathic medical specialist. The platform disclaims all liability for effects resulting from the improper, unsupervised, or unauthorized use of suggested remedies.
          </Typography>
        </Box>

        <Divider />

        <Box>
          <Typography variant="h6" component="h3" className="font-semibold text-blue-900 mb-1">
            5. WAIVER AND LIMITATION OF LIABILITY
          </Typography>
          <ul className="list-disc pl-5 space-y-1 text-gray-700 text-sm">
            <li>
              <strong>a)</strong> The User assumes sole responsibility for all decisions made regarding their health before, during, and after the consultation.
            </li>
            <li>
              <strong>b)</strong> The application, its owners, administrators, and participating practitioners are hereby released from any civil, criminal, or administrative liability arising from the User’s reliance on information, guidance, or remedies recommended through the service.
            </li>
            <li>
              <strong>c)</strong> Individual outcomes of homeopathic treatment may vary. No guarantee of cure or specific health outcome for any condition is made.
            </li>
          </ul>
        </Box>

        <Divider />

        <Box>
          <Typography variant="h6" component="h3" className="font-semibold text-blue-900 mb-1">
            6. ACCURACY OF PROVIDED INFORMATION
          </Typography>
          <Typography variant="body2" className="text-gray-700">
            The User warrants that all medical history, symptoms, pre-existing conditions, and current treatments shared during the consultation or recorded on the platform are truthful, accurate, complete, and up to date. The platform is not responsible for recommendations made based on false, misleading, or omitted information.
          </Typography>
        </Box>

        <Divider />

        <Box className="bg-blue-50 p-4 rounded-lg border border-blue-200">
          <Typography variant="h6" component="h3" className="font-semibold text-blue-900 mb-1">
            7. INFORMED CONSENT AND EXPRESS ACCEPTANCE
          </Typography>
          <Typography variant="body2" className="text-gray-800 font-medium mb-2">
            By accepting this document, the User confirms that:
          </Typography>
          <ul className="list-disc pl-5 space-y-1 text-gray-800 text-sm">
            <li>They have read and fully understood this Medical Disclaimer.</li>
            <li>They understand that the administration of any recommended homeopathic remedy requires supervision by a qualified specialist.</li>
            <li>They have had the opportunity to ask questions regarding the scope and limitations of the homeopathic consultation.</li>
            <li>They freely and voluntarily consent to receive homeopathic guidance under the terms set forth herein.</li>
          </ul>
        </Box>
      </DialogContent>

      <DialogActions className="p-4 bg-slate-50 flex justify-between items-center">
        <Typography variant="caption" className="text-gray-500 font-mono">
          Doc Version: v1.2-2026
        </Typography>
        <div className="flex gap-2">
          <Button variant="outlined" color="inherit" onClick={onClose}>
            Close
          </Button>
          {onAccept && (
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                onAccept();
                onClose();
              }}
            >
              I Agree & Accept
            </Button>
          )}
        </div>
      </DialogActions>
    </Dialog>
  );
};

export default MedicalDisclaimerModal;
